/**
 * MIT License
 *
 * Copyright (c) 2023 Mario Döring
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { ref, isRef, readonly, reactive, toRef, onMounted } from 'vue';
import { request } from '../api/client';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import translator from '@/locale/translator';
import { useAutopilotForm } from '@/composables/useAutopilotForm';

import type { Ref, DeepReadonly, UnwrapNestedRefs } from 'vue';
import type { ClientError, RequestMethod } from '@/api/client';

export type StringMap = { [key: string]: any };

type RemoteFormOptions = {
  /**
   * The REST endpoint base that is used to generate a few URLs:
   *
   * example: /v1/users/{id}
   * generates:
   *   GET /v1/users/42
   *   POST /v1/users
   *   PUT /v1/users/42
   *   DELETE /v1/users/42
   */
  endpoint: string | Ref<string>;

  /**
   * Query paramters that should be attached to all requests
   * Can also be a reference.
   */
  queryParameters: StringMap | Ref<StringMap>;

  /**
   * Custom object key extractor
   */
  getObjetKey: null | (() => string);

  /**
   * Allows you to return a custom HTTP method for create operations
   */
  getCreateHTTPMethod: null | (() => RequestMethod);

  /**
   * Allows you to return a custom HTTP method for update operations
   */
  getUpdateHTTPMethod: null | (() => RequestMethod);

  /**
   * Custom error message handler
   */
  handleErrorMessage: null | ((string) => void);

  /**
   * Custom success message handler
   */
  handleSuccessMessage: null | ((string) => void);

  /**
   * Default save error message when no message is provided from the server
   */
  defaultSavingErrorMessage: string;

  /**
   * Default success message if nothing is provided by the server
   */
  defaultSavingSuccessMessage: string;

  /**
   * The function that extract the actual data object
   * from a response object
   */
  extractObject(responseData: StringMap): StringMap;

  /**
   * The function that is used as an event after data is fetched and extracted
   * from a response object
   */
  afterFetch(responseData: StringMap): void;

  /**
   * Allows you to change data before its beeing send to
   * the remote endpoint.
   *
   * If you return null, this will cancel the saving process..
   */
  prepareDataForSave(serializedData: StringMap): StringMap | null;

  /**
   * Returns a new empty state object for the page
   * This is mainly used when a new API objects should be created as your defaults
   * and to ensure types etc...
   */
  createObject(): StringMap;

  /** Autopilot key. When set, exposes the form to autopilot (read + set_fields + save). */
  autopilotKey?: string;
  /** Human-readable description of what the form edits, fed into the LLM context. */
  autopilotDescription?: string | (() => string);
  /** Maps the form object into the shape surfaced in autopilot context. Defaults to the raw object. */
  autopilotTransformer?: (object: any) => Record<string, any>;
  /** JSON-schema properties describing the editable fields accepted by the set_fields function. */
  autopilotFields?: () => Record<string, any>;
  /** Overrides the autopilot save action. Defaults to `rfSaveRemote()`. */
  autopilotSave?: () => Promise<void> | void;
  /** Gates the autopilot save behind a user confirmation prompt. */
  autopilotSaveSensitive?: boolean;
  /** Overrides how autopilot applies a set_fields patch. Defaults to Object.assign on rfObject. */
  autopilotSetFields?: (patch: Record<string, any>) => void;
};

export function remoteFormOptions(endpoint: string | Ref<string>): RemoteFormOptions {
  return {
    endpoint: endpoint,
    queryParameters: {},
    getObjetKey: null,
    getCreateHTTPMethod: null,
    getUpdateHTTPMethod: null,
    handleErrorMessage: null,
    handleSuccessMessage: null,
    defaultSavingErrorMessage: translator.get('data.save_failed'),
    defaultSavingSuccessMessage: translator.get('data.saved'),
    extractObject: (responseData: StringMap): StringMap => {
      return (responseData.data ?? responseData) as StringMap;
    },
    afterFetch: () => {},
    prepareDataForSave: (serializedData: StringMap): StringMap => {
      return serializedData;
    },
    createObject: (): StringMap => {
      return <StringMap>{};
    },
  };
}

type RemoteFormState = {
  isLoading: Ref<boolean>;
  isNewObject: Ref<boolean>;
  errors: DeepReadonly<StringMap>;
  responseData: DeepReadonly<StringMap>;
};

export function useRemoteForm<ObjectType>(options: RemoteFormOptions) {
  const route = useRoute();
  const router = useRouter();
  const toast = useToast();

  const rfObject: Ref<ObjectType | undefined> = ref();
  const responseData = ref({} as any);
  const responseErrors = ref({} as any);

  const rfState: UnwrapNestedRefs<RemoteFormState> = reactive({
    isLoading: ref(true),
    isNewObject: ref(true),
    errors: readonly(responseErrors),
    responseData: readonly(responseData),
  });

  /**
   * Returns the guessed primary key of the object based
   * on the current route.
   * When you use a route like the following example all should work just fine:
   *   /v1/myendpoint/records_example/:id
   *
   * if null is returned the assumption is that this is a new object.
   */
  function rfGetObjectKey(): string | null {
    const key = route?.params?.id ?? route?.params?.key ?? null;

    if (typeof key !== 'string' || !key) {
      return null;
    }

    if (key === 'null' || key === '+') {
      return null;
    }

    return key;
  }

  /**
   * Returns a string of the current endpoint to the sepcific object
   */
  function rfGetRemoteEndpointString(): string {
    let endpoint = options.endpoint;
    if (isRef(endpoint)) {
      endpoint = endpoint.value;
    }

    // replace the key
    return endpoint.replace(/\{(key|id)\}/, rfGetObjectKey() || '');
  }

  /**
   * Returns a string of the enpoint where new object can be created
   */
  function rfGetRemoteCreateEnpointString(): string {
    return rfGetRemoteEndpointString().replace(/\/+$/, '');
  }

  /**
   * Returns the current required query paramters
   */
  function rfGetQueryParameters(): StringMap {
    // there has to be a better way...
    if (isRef(options.queryParameters)) {
      return JSON.parse(JSON.stringify(options.queryParameters)) as StringMap;
    }
    return options.queryParameters;
  }

  function rfGetErrorsFromResponse(response: StringMap): StringMap {
    return (response?.errors ?? {}) as StringMap;
  }

  function rfGetObjectKeyFromResponse(response: StringMap): string | number | null {
    return response?.data?.id ?? response?.data?.key ?? response?.id ?? response?.key ?? null;
  }

  /**
   * Fetches the objects current data
   */
  function rfFetchRemote() {
    const endpoint = rfGetRemoteEndpointString();
    const objectKey = rfGetObjectKey();
    rfState.isNewObject = false;

    if (!objectKey) {
      rfObject.value = options.createObject() as ObjectType;
      rfState.isNewObject = true;
      rfState.isLoading = false;
      // afterFetch for a new entity runs from onMounted so route-readiness waits until setup registered the form tools
      return;
    }

    if (!endpoint) {
      console.error('Cannot fetch "RemoteForm" without a valid endpoint.');
      return;
    }

    rfState.isLoading = true;

    const requestParameters = rfGetQueryParameters();

    request('GET', endpoint, requestParameters)
      .then((response) => {
        responseData.value = response;
        rfObject.value = options.extractObject(response as StringMap) as ObjectType;
        options.afterFetch(rfObject.value as StringMap);
      })
      .catch((e) => {
        console.error(e);
        options.afterFetch(rfObject.value as StringMap);
      })
      .finally(() => {
        rfState.isLoading = false;
      });
  }

  /**
   * Saves the current state
   */
  function rfSaveRemote(argument: any) {
    const data = options.prepareDataForSave(JSON.parse(JSON.stringify(rfObject.value)));

    const finaliseAndStopLoading = () => {
      rfState.isLoading = false;

      // simple loading button pass
      if (typeof argument === 'function') {
        argument();
      }
    };

    // when the prepare function returns false in any way something went wrong
    if (data === null) {
      return finaliseAndStopLoading();
    }

    const endpoint = rfState.isNewObject ? rfGetRemoteCreateEnpointString() : rfGetRemoteEndpointString();
    const method = rfState.isNewObject
      ? options.getCreateHTTPMethod
        ? options.getCreateHTTPMethod()
        : 'POST'
      : options.getUpdateHTTPMethod
        ? options.getUpdateHTTPMethod()
        : 'PUT';

    // run the request
    const requestParameters = rfGetQueryParameters();
    return request(method, endpoint, requestParameters, data, { skipDefaultErrorHandler: true })
      .then((response) => {
        responseErrors.value = {};
        responseData.value = response;
        rfObject.value = options.extractObject(response as StringMap) as ObjectType;
        rfState.isNewObject = false;
        rfState.isLoading = false;

        let successMessageHandler = options.handleSuccessMessage;
        if (!successMessageHandler) {
          successMessageHandler = (message) => {
            toast.success(message, { icon: 'ti ti-check' });
          };
        }

        successMessageHandler(options.defaultSavingSuccessMessage);

        // try to redirect to the correct page
        // this assumes that we follow the pattern of marking the new page
        // with "+" instead of the object id
        if (route.path.includes('+')) {
          const newId = rfGetObjectKeyFromResponse(response);
          router.push({ path: route.path.replace(/\+/, String(newId)) });
        }
      })
      .catch((e: ClientError) => {
        const errorMessageHandler =
          options.handleErrorMessage ??
          ((message) => {
            toast.error(message, { icon: 'ti ti-x' });
          });

        if (e.responseJson) {
          responseErrors.value = rfGetErrorsFromResponse(e.responseJson);
          // if the response contains an error message print it aswell
          if (e.responseJson.message) {
            errorMessageHandler(e.responseJson.message);
          } else {
            errorMessageHandler(options.defaultSavingErrorMessage);
          }
        } else {
          errorMessageHandler(options.defaultSavingErrorMessage);
        }
        throw e;
      })
      .finally(() => {
        finaliseAndStopLoading();
      });
  }

  /**
   * Returns an array of errors for a given field
   * Promises an array of string, so it will handle undefined and null
   */
  function rfGetErrors(fieldName: string): string[] {
    const fieldErrors = rfState.errors[fieldName] ?? [];

    // we have to handle to possiblity of string keys here,
    // as depending of the backend version keys are returned for every error.
    if (Array.isArray(fieldErrors)) {
      return fieldErrors;
    } else {
      return Object.values(fieldErrors);
    }
  }

  /**
   * Forward exposed functions
   */
  const rfFunctions = {
    save: rfSaveRemote,
    errors: rfGetErrors,
  };

  // new/`+` entities skip the GET; fire afterFetch on mount so a navigating function
  // (goto_create_*) is held until this form's set_fields/save tools exist
  onMounted(() => {
    if (rfState.isNewObject) {
      options.afterFetch(rfObject.value as StringMap);
    }
  });

  // expose this form to autopilot when an autopilot-key is provided (no-op otherwise)
  useAutopilotForm({
    key: () => options.autopilotKey,
    description: () =>
      typeof options.autopilotDescription === 'function'
        ? options.autopilotDescription()
        : options.autopilotDescription,
    transformer: options.autopilotTransformer,
    fields: options.autopilotFields,
    object: rfObject,
    errors: responseErrors,
    isNew: toRef(rfState, 'isNewObject'),
    isLoading: toRef(rfState, 'isLoading'),
    setFields: (patch) => {
      options.autopilotSetFields?.(patch);
      if (rfObject.value && typeof rfObject.value === 'object') {
        Object.assign(rfObject.value as object, patch);
      }
    },
    save: options.autopilotSave ?? (() => rfSaveRemote(undefined)),
    saveSensitive: options.autopilotSaveSensitive,
  });

  // run inital fetch
  rfFetchRemote();

  return { rfObject, rfState, rfFunctions };
}
